import loadable from '@loadable/component';
import { h } from 'preact';
import UniversalRouter from 'universal-router';

import loggerGenerator from '@travel/logger';
import { parseSearchQuery } from '@travel/traveler-core/utils/jrdpHelper';
import { parseQueryString } from '@travel/utils';

import Loading from 'pages/HotelList/components/Loading';
import HotelListResolver from 'pages/HotelList/resolver';
import HotelListMapResolver from 'pages/HotelListMap/resolver';
import PlanDetailResolver from 'pages/PlanDetail/resolver';
import PlansListResolver from 'pages/PlansList/resolver';
import SkeletonPage from 'pages/SkeletonPage';
import TopPageResolver from 'pages/Top/resolver';

import paths from 'constants/paths';
import App from 'core/app';
import AppResolver from 'core/resolver';
import { normalizePathIdentifier } from 'utils/search';
import { universalRouterProps } from './types';

const logger = loggerGenerator();

const loadOptions = {
  fallback: <SkeletonPage />,
};

export default (props: universalRouterProps) => {
  const isServer = props.isServer || false;

  const routes = [
    {
      path: '/',
      async action(context: any) {
        let children;

        try {
          children = await context.next();
        } catch (e) {
          logger.error(e);
          children = null;
        }

        if (isServer) {
          await AppResolver(props);
        } else if (!context.skipResolver) {
          AppResolver(props);
        }

        return (
          <App {...context} key="app">
            {children}
          </App>
        );
      },
      children: [
        {
          path: paths.top.path,
          async action(context: any) {
            const TopPage = loadable(
              () => import(/* webpackChunkName: "pages/Top" */ 'pages/Top'),
              loadOptions,
            );

            if (isServer) {
              await TopPageResolver(props);
            } else if (!context.skipResolver) {
              TopPageResolver(props);
            }

            return <TopPage {...context} />;
          },
        },
        {
          path: paths.providerList.path,
          async action(context: any) {
            const HotelList = loadable(
              () => import(/* webpackChunkName: "pages/HotelList" */ 'pages/HotelList'),
              {
                ...loadOptions,
                fallback: props.store.getState().hotelList.isFirstTimeLoading ? (
                  <Loading />
                ) : (
                  <SkeletonPage />
                ),
              },
            );

            if (!context.params.pageIdentifier) {
              const ErrorPage = loadable(() =>
                import(/* webpackChunkName: "pages-ErrorPage" */ 'pages/ErrorPage'),
              );
              props.res?.status(400);

              return <ErrorPage />;
            }
            const { isTravelRakutenOrigin } = props.store.getState().router;
            const { normalizePath, pageNumber } = normalizePathIdentifier(
              context.params.pageIdentifier,
            );
            const params = {
              pageIdentifier: normalizePath,
              pageNumber,
            };

            if (isServer && !isTravelRakutenOrigin) {
              await HotelListResolver(props, params, context.query);
            } else if (!context.skipResolver || isTravelRakutenOrigin) {
              HotelListResolver(props, params, context.query);
            }

            return <HotelList {...context} params={params} />;
          },
        },

        {
          path: paths.providerListMap.path,
          async action(context: any) {
            const HotelListMap = loadable(
              () => import(/* webpackChunkName: "pages/HotelListMap" */ 'pages/HotelListMap'),
              loadOptions,
            );

            const { normalizePath, pageNumber } = normalizePathIdentifier(
              context.params.pageIdentifier,
            );

            const params = {
              pageIdentifier: normalizePath,
              pageNumber,
            };

            if (!isServer) {
              HotelListMapResolver(props, params, context.query);
            }

            return <HotelListMap {...context} params={params} />;
          },
        },

        {
          path: paths.plansList.path,
          async action(context: any) {
            const PlansList = loadable(
              () => import(/* webpackChunkName: "pages/PlansList" */ 'pages/PlansList'),
              loadOptions,
            );

            const { isTravelRakutenOrigin } = props.store.getState().router;

            const { pageNumber = 1 } = normalizePathIdentifier(
              context.params?.pageNum || context.params.pageIdentifier,
            );
            const params = {
              pageIdentifier: context.params.pageIdentifier,
              pageNumber,
            };

            if (isServer && !isTravelRakutenOrigin) {
              await PlansListResolver(props, params, context.query);
            } else if (!context.skipResolver || isTravelRakutenOrigin) {
              PlansListResolver(props, params, context.query);
            }
            return <PlansList {...context} params={params} />;
          },
        },
        {
          path: paths.planDetail.path,
          async action(context: any) {
            const PlanDetail = loadable(
              () => import(/* webpackChunkName: "pages/PlanDetail" */ 'pages/PlanDetail'),
              loadOptions,
            );

            if (isServer) {
              await PlanDetailResolver(props, context.params, context.query);
            } else if (!context.skipResolver) {
              PlanDetailResolver(props, context.params, context.query);
            }

            return <PlanDetail {...context} />;
          },
        },
        {
          // also resolve paths.notFound.path
          path: '(.*)',
          async action() {
            const ErrorPage = loadable(() =>
              import(/* webpackChunkName: "pages-ErrorPage" */ 'pages/ErrorPage'),
            );
            props.res?.status(400);
            return <ErrorPage />;
          },
        },
      ],
    },
  ];
  return new UniversalRouter(routes, {
    resolveRoute(context, params) {
      if (typeof context.route.action === 'function') {
        if (context.search) {
          const searchQuery = (context.search as string).replace(/\?$/, '');

          const defaultQuery = parseQueryString(searchQuery);
          context.query = { ...defaultQuery, ...parseSearchQuery(defaultQuery) };
        }

        return context.route.action(context, params);
      }
      return undefined;
    },
  });
};
